import React, { useEffect, useState } from "react";
import { useGeoApps, useAppSettings } from "@mapgear/geoapps-ui-framework";
import { FeatureInfoType } from "../types/FeatureInfoType";
import { FeatureInfoContext } from "./FeatureInfoContext";
import ImageLayer from "ol/layer/Image.js";
import { TimeSlider } from "./TimeSlider";
import { Sidebar } from "./Sidebar";
import { Layers } from "./Layers";
import XYZ from "ol/source/XYZ";
import TileLayer from "ol/layer/Tile";
import TileWMS from 'ol/source/TileWMS.js';
import proj4 from 'proj4';
import { register } from 'ol/proj/proj4';

export const AppLayout = () => {
	const { geoapps } = useGeoApps();
	const [loading, setLoading] = useState(true);
	const [highlightLayer, setHighlightLayer] = useState<any>();
	const [helpLayer, setHelpLayer] = useState<any>();
	const [years, setYears] = useState<[number | undefined, number | undefined]>([1832, 1832]);
	const [featureInfo, setFeatureInfo] = useState<FeatureInfoType>({ nr: "", geom: "", from: 1832, until: 1832, toggle: 0, kt: "" });
	const [cadastralLayers, setCadastralLayers] = useState<any[]>([]);
	const { appSettings } = useAppSettings();
	const [map, setMap] = useState<any>();
	const cLayerUrls: {
		url: string,
		attributions: string
		name: string
		title?: string
		extent?: [number, number, number, number]
	}[] = [
			{ url: "https://www.atlasavereest.nl/kka1832v2/{z}/{x}/{y}.jpg", attributions: "Kadaster 1832 v0.2", name: "kaart 1832", extent: [701614.2451786917, 6903548.256516629, 728914.1257813432, 6920302.805057498] },
			{ url: "https://www.atlasavereest.nl/kka1860v2/{z}/{x}/{y}.jpg", attributions: "Kadaster 1862 v0.1", name: "kaart 1860", extent: [701614.2451786917, 6903548.256516629, 728914.1257813432, 6920302.805057498] },
			{ url: "https://www.atlasavereest.nl/kka1890v2/{z}/{x}/{y}.jpg", attributions: "Kadaster 1862 v0.1", name: "kaart 1888", extent: [701614.2451786917, 6903548.256516629, 728914.1257813432, 6920302.805057498] },
			{ url: "https://www.atlasavereest.nl/kka1966v3/{z}/{x}/{y}.jpg", attributions: "Dedemsvaart 1966 v0.3", name: "kaart 1966", extent: [716281.8126051042, 6907614.235017365, 723829.941997833, 6912276.84339399] },
			{ url: "https://geodata.nationaalgeoregister.nl/kadastralekaart/wms/v4_0", attributions: "", name: "kaart BRK" },
			{ url: "https://www.atlasavereest.nl/markten1810v0/{z}/{x}/{y}.png", attributions: "Markten 1810 v0", name: "Markten kaart" },
			{ url: "https://www.atlasavereest.nl/venen1820v0/{z}/{x}/{y}.png", attributions: "Venen 1820 v0", name: "Kaart der Veenen" },
			{ url: "https://www.atlasavereest.nl/gem1856v0/{z}/{x}/{y}.png", attributions: "Gemeentekaart 1856 v0", name: "Gemeente Avereest 1856", extent: [701614.2451786917, 6903548.256516629, 728914.1257813432, 6920302.805057498] },
		];

	// Define 28992 projection
	useEffect(() => {
		const projKey = "EPSG:28992";
		proj4.defs(projKey, "+proj=sterea +lat_0=52.15616055555555 +lon_0=5.38763888888889 +k=0.9999079 +x_0=155000 +y_0=463000 +ellps=bessel +towgs84=565.417,50.3319,465.552,-0.398957,0.343988,-1.8774,4.0725 +units=m +no_defs");
		register(proj4);
	}, []);

	// Init map
	useEffect(() => {
		if (!appSettings) return;
		const map = geoapps.AddMap("map", appSettings.mapId);
		setMap(map);

	}, [appSettings])

	// Init layers
	useEffect(() => {
		if (!map) return;

		// Load map
		map.onLoaded.add(() => {
			map.BaseLayers.onLoaded.add(async () => {

				// Add highlightlayer
				const highlightLayer = map.Layers.CreateLayer({
					type: "vector",
					zIndex: 99999
				});
				map.Layers.AddLayer(highlightLayer);
				setHighlightLayer(highlightLayer);

				// Add help layer
				const mapObj = map.MapEngine.Map.GetInternalMapObject();
				const layer = new ImageLayer({
					zIndex: 9,
				});
				mapObj.addLayer(layer);
				setHelpLayer(layer);

				// Create cadastral layers
				const cadastralLayers = cLayerUrls.map(layer => {
					if (layer.name === "kaart BRK") {
						return createWMSLayer(layer.url, "kadastralekaart", layer.attributions, layer.name);
					} else {
						return createTileLayer(layer.url, layer.attributions, layer.name, layer.extent ? layer.extent : undefined)
					}
				});
				setCadastralLayers(cadastralLayers);

				setLoading(false);
			});
		});

	}, [map]);

	/**
	 * Creates a new tilelayer
	 * @param url The url of the tilelayer
	 */
	const createTileLayer = (url: string, attribution: string, name: string, extent: [number, number, number, number] | undefined) => {
		// Create new Tile Layer
		const layer = new TileLayer({
			minZoom: 10,
			maxZoom: 25,
			visible: false,
			zIndex: 8,
			opacity: 1,
			extent: extent,
			name: name,
			preload: 0,
			source: new XYZ({
				url: url,
				crossOrigin: "anonymous",
				attributions: attribution,
			})
		});

		// Add layer to map
		const mapObj = map.GetInternalMapObject();
		mapObj.addLayer(layer);

		return layer;
	};

	/**
	 * Create WMS layer
	 * @param url url of the wms layer
	 * @param layer layer name of the wms layer
	 * @param attribution attribution
	 * @param name name
	 */
	const createWMSLayer = (url: string, layer: string, attribution: string, name: string) => {
		// Create layer
		const layerObj = new TileLayer({
			zIndex: 9,
			visible: false,
			name: name,
			source: new TileWMS({
				url: url,
				params: { "LAYERS": layer, "TILED": true },
				attributions: attribution,
			}),
		});

		// Add layer to map
		const mapObj = map.GetInternalMapObject();
		mapObj.addLayer(layerObj);

		return layerObj;
	};

	return (
		<div className="layout">
			<div id="map" className="map"></div>
			{loading ? <div>Laden...</div> :
				<>
					<FeatureInfoContext.Provider value={{ cadastralLayers: cadastralLayers, helpLayer: helpLayer, highlightLayer: highlightLayer, years: years, setYears: setYears, featureInfo: featureInfo, setFeatureInfo: setFeatureInfo }}>
						<Sidebar />
						<div className="layer-panel">
							<Layers />
						</div>
						<TimeSlider />
					</FeatureInfoContext.Provider>
				</>
			}
		</div >
	);
};
